import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { 
  Calculator, IndianRupee, TrendingUp, Home, Clock,
  Loader2, RefreshCw, CheckCircle, AlertCircle, Lightbulb
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PropertyValuation({ 
  location, 
  propertyType = "residential",
  carpetArea,
  builtUpArea,
  bedrooms,
  bathrooms,
  floorNumber,
  totalFloors,
  ageOfProperty,
  furnishing,
  amenities = []
}) {
  const [valuation, setValuation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateValuation = async () => {
    if (!location || location.trim().length < 3) {
      toast.error("Please enter a valid location first");
      return;
    }
    if (!carpetArea || parseFloat(carpetArea) <= 0) {
      toast.error("Please enter carpet area in Specifications tab");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/ai/property-valuation`,
        { 
          location, 
          property_type: propertyType,
          carpet_area: parseFloat(carpetArea),
          built_up_area: builtUpArea ? parseFloat(builtUpArea) : null,
          bedrooms: bedrooms ? parseInt(bedrooms) : null,
          bathrooms: bathrooms ? parseInt(bathrooms) : null,
          floor_number: floorNumber ? parseInt(floorNumber) : null,
          total_floors: totalFloors ? parseInt(totalFloors) : null,
          age_of_property: ageOfProperty || null,
          furnishing: furnishing || null,
          amenities: amenities || []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setValuation(response.data.valuation);
        toast.success("Property valuation generated!");
      } else {
        setError("Failed to generate valuation");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate valuation");
      toast.error("Failed to generate valuation");
    } finally {
      setLoading(false);
    }
  };

  const formatINR = (amount) => {
    if (!amount || isNaN(amount)) return "N/A";
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence === "high") return "text-green-600 bg-green-100";
    if (confidence === "medium") return "text-yellow-600 bg-yellow-100";
    return "text-red-600 bg-red-100";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-slate-900">AI Property Valuation</h3>
        </div>
        <Button
          type="button"
          onClick={generateValuation}
          disabled={loading || !location || !carpetArea}
          className="bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white"
          data-testid="generate-valuation-btn"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Calculating...
            </>
          ) : valuation ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Recalculate
            </>
          ) : (
            <>
              <Calculator className="h-4 w-4 mr-2" />
              Get Valuation
            </>
          )}
        </Button>
      </div>

      {/* Property Summary */}
      {(location || carpetArea) && (
        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
          {location && <span className="flex items-center gap-1"><Home className="h-4 w-4" /> {location}</span>}
          {carpetArea && <span>• {carpetArea} sq ft</span>}
          {bedrooms && <span>• {bedrooms} BHK</span>}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="p-8 bg-gradient-to-br from-blue-50 to-blue-50 rounded-lg border border-emerald-200">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-emerald-200 rounded-full animate-pulse"></div>
              <Calculator className="h-8 w-8 text-amber-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-bounce" />
            </div>
            <p className="text-blue-700 font-medium">AI is calculating property value...</p>
            <p className="text-sm text-amber-500">Analyzing market data for {location}</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {/* Missing Data State */}
      {(!location || !carpetArea) && !loading && !valuation && (
        <div className="p-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200 text-center">
          <Calculator className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Fill in Location and Carpet Area to get AI valuation</p>
          <p className="text-xs text-slate-400 mt-1">More details = more accurate valuation</p>
        </div>
      )}

      {/* Valuation Results */}
      {valuation && !loading && (
        <div className="space-y-6">
          {/* Main Valuation Card */}
          {valuation.estimated_value && (
            <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-50 rounded-lg border border-emerald-200">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-sm font-medium text-blue-700 uppercase tracking-wide">Estimated Market Value</h4>
                  <p className="text-4xl font-bold text-blue-800 mt-2">
                    {formatINR(valuation.estimated_value.recommended)}
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    Range: {formatINR(valuation.estimated_value.min)} - {formatINR(valuation.estimated_value.max)}
                  </p>
                </div>
                {valuation.estimated_value.confidence && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getConfidenceColor(valuation.estimated_value.confidence)}`}>
                    <CheckCircle className="h-4 w-4 inline mr-1" />
                    {valuation.estimated_value.confidence} confidence
                  </span>
                )}
              </div>
              {valuation.valuation_summary && (
                <p className="text-blue-700 text-sm border-t border-emerald-200 pt-4 mt-4">
                  {valuation.valuation_summary}
                </p>
              )}
            </div>
          )}

          {/* Price Per Sqft */}
          {valuation.price_per_sqft && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 uppercase">Your Rate/sq ft</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  ₹{valuation.price_per_sqft.carpet_area?.toLocaleString('en-IN') || 'N/A'}
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 uppercase">Market Average</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  ₹{valuation.price_per_sqft.market_average?.toLocaleString('en-IN') || 'N/A'}
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 uppercase">Premium/Discount</p>
                <p className={`text-2xl font-bold mt-1 ${valuation.price_per_sqft.premium_adjustment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {valuation.price_per_sqft.premium_adjustment >= 0 ? '+' : ''}{valuation.price_per_sqft.premium_adjustment}%
                </p>
              </div>
            </div>
          )}

          {/* Rental Estimate */}
          {valuation.rental_estimate && (
            <div className="p-5 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-3">
                <IndianRupee className="h-5 w-5 text-blue-600" />
                <h4 className="font-semibold text-blue-900">Rental Potential</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-blue-600 uppercase">Monthly Rent</p>
                  <p className="text-xl font-bold text-blue-800">{formatINR(valuation.rental_estimate.monthly_rent)}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-600 uppercase">Annual Yield</p>
                  <p className="text-xl font-bold text-blue-800">{valuation.rental_estimate.rental_yield}%</p>
                </div>
              </div>
            </div>
          )}

          {/* Market Comparison */}
          {valuation.market_comparison && (
            <div className="p-5 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-5 w-5 text-purple-500" />
                <h4 className="font-semibold text-slate-900">Market Comparison</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {valuation.market_comparison.similar_properties_range && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Similar Properties</p>
                    <p className="text-sm font-medium text-slate-700 mt-1">{valuation.market_comparison.similar_properties_range}</p>
                  </div>
                )}
                {valuation.market_comparison.days_on_market && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Expected Time to Sell</p>
                    <p className="text-sm font-medium text-slate-700 mt-1 flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {valuation.market_comparison.days_on_market}
                    </p>
                  </div>
                )}
                {valuation.market_comparison.demand_supply && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Market Condition</p>
                    <p className="text-sm font-medium text-slate-700 mt-1 capitalize">
                      {valuation.market_comparison.demand_supply.replace(/_/g, ' ')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {valuation.recommendations && valuation.recommendations.length > 0 && (
            <div className="p-5 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="h-5 w-5 text-amber-600" />
                <h4 className="font-semibold text-amber-900">Value Maximization Tips</h4>
              </div>
              <ul className="space-y-2">
                {valuation.recommendations.map((rec, idx) => (
                  <li key={idx} className="text-sm text-amber-800 flex items-start">
                    <span className="text-amber-500 mr-2 font-bold">{idx + 1}.</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
